// Media bin: every asset in the project (video + audio + generated items), shown
// as a thumbnail grid left of the Program monitor. Double-click an item to drop
// it on the timeline at the playhead.
import { Store } from "../state/store";
import { el } from "../util";
import { openContextMenu } from "./modal";
import { drawGenerator, GENERATOR_BADGE, GENERATOR_LABELS } from "../video/generators";

const fmtDur = (s: number) => { s = Math.max(0, s || 0); const m = Math.floor(s / 60); return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`; };

export type NewItemKind = "color" | "gradient" | "bars" | "title" | "lower" | "subtitle" | "countdown";

export class MediaBin {
  el = el("div", { class: "media-bin" });
  private listEl = el("div", { class: "media-list" });

  constructor(private store: Store, private cb: {
    addToTimeline: (assetId: string) => void;
    deleteAsset: (assetId: string) => void;
    newItem: (kind: NewItemKind) => void;
    editItem: (assetId: string) => void;
    importMedia: () => void;
  }) {
    const newBtn = el("button", { class: "media-new", text: "＋ New item ▾", attrs: { title: "Create a color matte, bars & tone, title, countdown…" } });
    newBtn.addEventListener("click", () => {
      const r = newBtn.getBoundingClientRect();
      openContextMenu(r.left, r.bottom + 4, [
        { label: "■  Color matte (black, …)", onClick: () => cb.newItem("color") },
        { label: "◩  Gradient", onClick: () => cb.newItem("gradient") },
        { label: "▥  Bars & tone", onClick: () => cb.newItem("bars") },
        { label: "T  Title", onClick: () => cb.newItem("title") },
        { label: "T  Lower third", onClick: () => cb.newItem("lower") },
        { label: "T  Subtitle", onClick: () => cb.newItem("subtitle") },
        { label: "⏲  Countdown leader", onClick: () => cb.newItem("countdown") },
      ]);
    });
    const importBtn = el("button", { class: "media-new", text: "⇪ Import", attrs: { title: "Import video or audio files (or drag them in)" } });
    importBtn.addEventListener("click", () => cb.importMedia());
    const bar = el("div", { class: "media-bar" }, [el("b", { text: "Media" }), el("div", { class: "spacer" }), newBtn, importBtn]);
    this.el.append(bar, this.listEl);
    this.build();
    // Only the asset set changing (or a project load) affects the bin — not every
    // clip edit — so we avoid rebuilding thumbnails during timeline interaction.
    store.on("change", ({ scope }) => { if (scope === "assets" || scope === "project") this.build(); });
  }

  private build() {
    this.listEl.innerHTML = "";
    // Only top-level media (a video's extracted audio is hidden child assets).
    const assets = this.store.project.assets.filter((a) => !a.ownerId);
    if (!assets.length) {
      this.listEl.append(el("p", { class: "media-empty hint", text: "No media yet.\nImport video/audio, drag a file in,\nor make a ＋ New item (color, bars, title…)." }));
      return;
    }
    for (const a of assets) {
      const item = el("div", { class: "media-item", attrs: { draggable: "true" } });
      item.title = "Double-click or drag onto the timeline to use it";
      item.addEventListener("dragstart", (e) => { e.dataTransfer?.setData("application/x-webcut-asset", a.id); if (e.dataTransfer) e.dataTransfer.effectAllowed = "copy"; });
      const nAudio = a.audioAssetIds?.length ?? 0;
      const gen = a.generator;
      const thumb = el("div", { class: "media-thumb" + (a.kind === "video" ? "" : " audio") + (gen ? " gen" : "") });
      if (a.kind === "video") {
        const cv = document.createElement("canvas"); cv.width = 128; cv.height = 72;
        const g = cv.getContext("2d")!;
        if (gen) drawGenerator(g, gen, cv.width, cv.height, gen.type === "countdown" ? 0.25 : 0);
        else {
          const src = this.store.video(a.id);
          if (src) {
            const draw = () => { try { g.drawImage(src.el, 0, 0, cv.width, cv.height); } catch {} };
            if (src.el.readyState >= 2) draw(); else src.el.addEventListener("loadeddata", draw, { once: true });
          }
        }
        thumb.append(cv);
      } else {
        thumb.append(el("span", { class: "media-ico", text: "🎵" }));
      }
      thumb.append(el("span", { class: "media-badge", text: gen ? GENERATOR_BADGE[gen.type] : a.kind === "video" ? "VIDEO" : "AUDIO" }));
      if (a.audioPending) thumb.append(el("span", { class: "media-pending", text: "⏳ audio…" }));
      else if (a.kind === "video" && a.audioNote) {
        const warn = el("span", { class: "media-pending noaudio", text: "🔇 no audio" });
        warn.title = a.audioNote;
        thumb.append(warn);
      }
      if (gen) {
        const edit = el("button", { class: "media-del media-edit", text: "✎", attrs: { title: `Edit this ${GENERATOR_LABELS[gen.type].toLowerCase()} (updates every clip using it)` } });
        edit.addEventListener("click", (e) => { e.stopPropagation(); this.cb.editItem(a.id); });
        thumb.append(edit);
      }
      const del = el("button", { class: "media-del", text: "✕", attrs: { title: "Remove from project (and the timeline)" } });
      del.addEventListener("click", (e) => { e.stopPropagation(); this.cb.deleteAsset(a.id); });
      thumb.append(del);
      const audioTag = a.audioPending ? " · ⏳" : nAudio > 1 ? ` · ${nAudio}🎵` : nAudio === 1 ? " · 🎵" : "";
      const sub = gen ? `${GENERATOR_LABELS[gen.type]} · ${fmtDur(a.defaultDuration ?? a.duration)}${audioTag}`
        : a.kind === "video" ? `${a.width}×${a.height} · ${fmtDur(a.duration)}${audioTag}` : fmtDur(a.duration);
      const meta = el("div", { class: "media-meta" }, [
        el("div", { class: "media-name", text: a.name }),
        el("div", { class: "media-sub", text: sub }),
      ]);
      item.append(thumb, meta);
      item.addEventListener("dblclick", () => this.cb.addToTimeline(a.id));
      if (gen) item.addEventListener("contextmenu", (e) => { e.preventDefault(); openContextMenu(e.clientX, e.clientY, [
        { label: "Edit…", onClick: () => this.cb.editItem(a.id) },
        { label: "Place at playhead", onClick: () => this.cb.addToTimeline(a.id) },
        { label: "Remove", onClick: () => this.cb.deleteAsset(a.id) },
      ]); });
      this.listEl.append(item);
    }
  }
}
